import axios, { type AxiosInstance } from "axios"
import config from "config"
import type { IDevice, IDeviceAuthenticate, IDeviceEvent, IUpdateDevice } from "types/deviceTypes"
import authService from "./authService"

class DeviceService {
  private http: AxiosInstance

  public constructor() {
    this.http = axios.create({
      baseURL: config.apiUrl,
      headers: {
        "Content-type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    })
  }

  public getDevices = async (): Promise<Array<IDevice>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<IDevice>>("/device", config)

    return data
  }

  public getOrganizationDevices = async (organizationId: string): Promise<Array<IDevice>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<IDevice>>(
      `organization/${organizationId}/device`,
      config,
    )

    return data
  }

  public getDevice = async (deviceId: string): Promise<IDevice> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<IDevice>(`device/${deviceId}`, config)

    return data
  }

  public updateDevice = async (deviceId: string, deviceUpdate: IUpdateDevice): Promise<IDevice> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.put(`device/${deviceId}`, deviceUpdate, config)

    return data
  }

  public deleteDevice = async (deviceId: string) => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.delete(`device/${deviceId}`, config)

    return resp
  }

  public getDeviceEvents = async (deviceId: string): Promise<Array<IDeviceEvent>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<IDeviceEvent>>(`device/${deviceId}/event`, config)

    return data
  }

  public deviceLogin = async (deviceLogin: IDeviceAuthenticate) => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.post<Array<IDevice>>("device/devicecode", deviceLogin, config)

    return data
  }
}

const deviceService = new DeviceService()

export default deviceService
